import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDealValue, parseDealValueToDollars } from "@/lib/formatDealValue";
import {
  computeMultiples, deriveEv, rolloverDollars, cashAtClosing, totalConsideration,
  formatMultipleRange, formatEvRange, type OfferTerms,
} from "@shared/offerMath";
import { useBulkAction, type OfferStage, type OfferTermsPayload } from "@/hooks/useBuyerTracker";

export interface OfferQueueItem {
  linkId: string;
  buyerName: string;
  /** Terms already saved for THIS stage (edit mode) */
  existing: OfferTerms | null;
  /** IOI terms, used to prefill + reference when entering the LOI */
  ioiRef: OfferTerms | null;
  /** Milestone timestamp already set → save terms without touching the date */
  alreadyMarked: boolean;
}

interface Props {
  open: boolean;
  stage: OfferStage;
  queue: OfferQueueItem[];
  clientId: string;
  /** Client's current EBITDA in dollars (null → dialog collects it) */
  ebitda: number | null;
  /** Backdate ("Mark as of…") — local-noon ISO passed through to the server */
  date?: string;
  onDone: () => void;
}

const money = (n: number) => formatDealValue(String(Math.round(n)));

export function OfferTermsDialog({ open, stage, queue, clientId, ebitda, date, onDone }: Props) {
  const bulk = useBulkAction(clientId);
  const [idx, setIdx] = useState(0);
  const [savedCount, setSavedCount] = useState(0);
  const [evLow, setEvLow] = useState("");
  const [evHigh, setEvHigh] = useState("");
  const [ebitdaInput, setEbitdaInput] = useState("");
  const [rollover, setRollover] = useState("");
  const [earnout, setEarnout] = useState("");
  const [earnoutMode, setEarnoutMode] = useState<"" | "in" | "top">("");
  const [sellerFin, setSellerFin] = useState("");
  const [notes, setNotes] = useState("");

  const item = queue[idx] as OfferQueueItem | undefined;
  const stageLabel = stage.toUpperCase();

  // Prefill per buyer: existing terms (edit) beats IOI reference (LOI entry) beats blank.
  // EV prefills re-derived at the CURRENT EBITDA — the multiple is the hard number.
  useEffect(() => {
    if (!open || !item) return;
    const src = item.existing ?? (stage === "loi" ? item.ioiRef : null);
    if (src) {
      const ev = deriveEv(src, ebitda);
      setEvLow(money(ev.low));
      // Single-value bids keep the high field blank
      setEvHigh(ev.low === ev.high ? "" : money(ev.high));
      setRollover(src.rolloverPct != null ? String(src.rolloverPct) : "");
      setEarnout(src.earnoutAmount != null ? money(src.earnoutAmount) : "");
      setEarnoutMode(src.earnoutAmount != null && src.earnoutAmount > 0 ? (src.earnoutInEv ? "in" : "top") : "");
      setSellerFin(src.sellerFinancingAmount != null ? money(src.sellerFinancingAmount) : "");
      setNotes(item.existing ? (src.notes ?? "") : "");
    } else {
      setEvLow(""); setEvHigh(""); setRollover(""); setEarnout("");
      setEarnoutMode(""); setSellerFin(""); setNotes("");
    }
    // Basis defaults to the client's current EBITDA — override it when the
    // buyer's bid was based on a different number and the multiple converts.
    setEbitdaInput(ebitda != null && ebitda > 0 ? money(ebitda) : "");
  }, [open, idx, item?.linkId]);

  useEffect(() => {
    if (open) { setIdx(0); setSavedCount(0); }
  }, [open]);

  const parsed = useMemo(() => {
    const toNum = (s: string) => { const d = parseDealValueToDollars(s); return d != null ? Number(d) : null; };
    const evLowD = toNum(evLow);
    // Blank high = a single-number bid: low and high collapse to what they bid
    const evHighD = evHigh.trim() === "" ? evLowD : toNum(evHigh);
    const eb = toNum(ebitdaInput);
    const rolloPct = rollover.trim() === "" ? null : Number(rollover);
    const earnoutD = toNum(earnout);
    const sellerD = toNum(sellerFin);
    return { evLowD, evHighD, eb, rolloPct, earnoutD, sellerD };
  }, [evLow, evHigh, ebitdaInput, rollover, earnout, sellerFin]);

  const { evLowD, evHighD, eb, rolloPct, earnoutD, sellerD } = parsed;
  const evValid = evLowD != null && evHighD != null && evLowD > 0 && evHighD >= evLowD;
  const ebValid = eb != null && eb > 0;
  const rolloverValid = rolloPct === null || (Number.isFinite(rolloPct) && rolloPct >= 0 && rolloPct <= 100);
  const earnoutNeedsMode = earnoutD != null && earnoutD > 0 && earnoutMode === "";
  const canSave = evValid && ebValid && rolloverValid && !earnoutNeedsMode && !bulk.isPending;

  const multiples = evValid && ebValid ? computeMultiples(evLowD!, evHighD!, eb!) : null;
  const preview = useMemo(() => {
    if (!evValid) return null;
    const t: OfferTerms = {
      multipleLow: multiples?.low ?? 0, multipleHigh: multiples?.high ?? 0,
      evLowEntered: evLowD!, evHighEntered: evHighD!, ebitdaAtEntry: eb ?? 0,
      rolloverPct: rolloverValid ? rolloPct : null,
      earnoutAmount: earnoutD, earnoutInEv: earnoutMode === "" ? null : earnoutMode === "in",
      sellerFinancingAmount: sellerD, notes: null,
    };
    const single = evLowD === evHighD;
    const pts = single ? [evLowD!] : [evLowD!, (evLowD! + evHighD!) / 2, evHighD!];
    return {
      labels: single ? ["Bid"] : ["Low", "Mid", "High"],
      ev: pts,
      rollover: pts.map((p) => rolloverDollars(t.rolloverPct, p)),
      cash: pts.map((p) => cashAtClosing(t, p)),
      total: pts.map((p) => totalConsideration(t, p)),
    };
  }, [evValid, evLowD, evHighD, eb, rolloPct, rolloverValid, earnoutD, earnoutMode, sellerD, multiples]);

  // Basis differs from the client's current EBITDA → the multiple converts
  const basisDiffers = eb != null && eb > 0 && ebitda != null && ebitda > 0 && Math.round(eb) !== Math.round(ebitda);

  const ioiRefLine = stage === "loi" && item?.ioiRef ? (() => {
    const ev = deriveEv(item.ioiRef!, ebitda);
    return `IOI was: ${formatEvRange(ev.low, ev.high)} (${formatMultipleRange(item.ioiRef!.multipleLow, item.ioiRef!.multipleHigh)})`;
  })() : null;

  const finish = (count: number) => {
    if (count > 0) {
      toast.success(`${stageLabel} terms saved for ${count} buyer${count === 1 ? "" : "s"}`);
    }
    onDone();
  };

  const save = () => {
    if (!item || !canSave) return;
    const terms: OfferTermsPayload = {
      evLow: evLowD!, evHigh: evHighD!, ebitda: eb!,
      rolloverPct: rolloPct,
      earnoutAmount: earnoutD,
      earnoutInEv: earnoutD != null && earnoutD > 0 ? earnoutMode === "in" : null,
      sellerFinancingAmount: sellerD,
      notes: notes.trim() || null,
    };
    const action = item.alreadyMarked
      ? { type: "terms" as const, stage, terms }
      : { type: "milestone" as const, milestone: stage, ...(date ? { date } : {}), terms };
    bulk.mutate({ linkIds: [item.linkId], action }, {
      onSuccess: () => {
        const done = savedCount + 1;
        setSavedCount(done);
        if (idx + 1 < queue.length) setIdx(idx + 1);
        else finish(done);
      },
      onError: (e) => toast.error(e.message || "Failed to save terms"),
    });
  };

  const cancel = () => {
    if (savedCount > 0) toast.info(`${stageLabel} terms saved for ${savedCount} of ${queue.length} buyers before cancel`);
    onDone();
  };

  if (!item) return null;

  const inputRow = (label: string, el: ReactNode, hint?: string) => (
    <div className="flex items-center gap-3">
      <div className="w-36 shrink-0 text-xs font-semibold text-slate-500">{label}</div>
      <div className="flex-1">{el}</div>
      {hint && <div className="w-40 shrink-0 text-right text-[11px] tabular-nums text-slate-400">{hint}</div>}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && cancel()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {stageLabel} Terms — {item.buyerName}
            {queue.length > 1 && (
              <span className="ml-2 text-sm font-normal text-slate-400">Buyer {idx + 1} of {queue.length}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {ioiRefLine && (
          <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-400">
            {ioiRefLine} — update anything that changed for the LOI.
          </div>
        )}

        <div className="space-y-3">
          {inputRow("Enterprise value *", (
            <div className="flex items-center gap-2">
              <Input className="h-8" placeholder="Bid, e.g. 8M" value={evLow} onChange={(e) => setEvLow(e.target.value)} />
              <span className="text-slate-400">–</span>
              <Input className="h-8" placeholder="High (optional)" value={evHigh} onChange={(e) => setEvHigh(e.target.value)} />
            </div>
          ))}
          <div className="text-[11px] text-slate-400">Leave the second field blank for a single-number bid.</div>

          {inputRow("Based on EBITDA *", (
            <div className="flex items-center gap-2">
              <Input className="h-8 w-28" placeholder="e.g. 5.2M" value={ebitdaInput} onChange={(e) => setEbitdaInput(e.target.value)} />
              {ebitda != null && ebitda > 0 && !basisDiffers && (
                <span className="text-[11px] text-slate-400">current — change if their bid used a different EBITDA</span>
              )}
              {basisDiffers && (
                <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400">
                  differs from current ({money(ebitda!)}) — multiple uses their basis, EV re-derives at current
                </span>
              )}
            </div>
          ))}

          {inputRow("Multiple", (
            <div className="text-sm font-bold text-slate-900 tabular-nums dark:text-slate-200">
              {multiples ? formatMultipleRange(multiples.low, multiples.high) : <span className="font-normal text-slate-400">enter EV + EBITDA</span>}
            </div>
          ))}

          <div className="border-t border-slate-100 dark:border-slate-900" />

          {inputRow("Rollover %", (
            <Input className="h-8 w-24" placeholder="e.g. 20" value={rollover} onChange={(e) => setRollover(e.target.value)} />
          ), preview && rolloPct != null && rolloverValid ? preview.rollover.map((v) => money(v)).join(" / ") : undefined)}
          {!rolloverValid && <div className="text-[11px] text-red-600 dark:text-red-400">Rollover must be 0–100%</div>}

          {inputRow("Earnout", (
            <div className="flex items-center gap-3">
              <Input className="h-8 w-28" placeholder="e.g. 1.5M" value={earnout} onChange={(e) => setEarnout(e.target.value)} />
              {earnoutD != null && earnoutD > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  <label className="flex cursor-pointer items-center gap-1">
                    <input type="radio" name="earnout-mode" checked={earnoutMode === "in"} onChange={() => setEarnoutMode("in")} />
                    Included in EV
                  </label>
                  <label className="flex cursor-pointer items-center gap-1">
                    <input type="radio" name="earnout-mode" checked={earnoutMode === "top"} onChange={() => setEarnoutMode("top")} />
                    On top of EV
                  </label>
                </div>
              )}
            </div>
          ))}
          {earnoutNeedsMode && <div className="text-[11px] text-amber-600 dark:text-amber-400">Select whether the earnout is included in EV or on top — it changes cash at closing.</div>}

          {inputRow("Seller financing", (
            <Input className="h-8 w-28" placeholder="e.g. 1M" value={sellerFin} onChange={(e) => setSellerFin(e.target.value)} />
          ))}

          {inputRow("Notes", (
            <Input className="h-8" placeholder="Optional — shows in Review Offers" value={notes} onChange={(e) => setNotes(e.target.value)} />
          ))}

          {preview && (
            <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-900">
              <table className="w-full text-[12px] tabular-nums">
                <thead>
                  <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400 dark:bg-slate-950/40">
                    <th className="p-1.5 text-left font-semibold"></th>
                    {preview.labels.map((l) => <th key={l} className="p-1.5 text-right font-semibold">{l}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-slate-100 dark:border-slate-900">
                    <td className="p-1.5 font-semibold text-slate-500">Enterprise value</td>
                    {preview.ev.map((v, i) => <td key={i} className="p-1.5 text-right font-semibold text-slate-900 dark:text-slate-200">{money(v)}</td>)}
                  </tr>
                  <tr className="border-t border-slate-100 dark:border-slate-900">
                    <td className="p-1.5 font-semibold text-slate-500">Cash at closing</td>
                    {preview.cash.map((v, i) => <td key={i} className="p-1.5 text-right font-bold text-green-700 dark:text-green-400">{money(v)}</td>)}
                  </tr>
                  {preview.total[0] != null && (
                    <tr className="border-t border-slate-100 dark:border-slate-900">
                      <td className="p-1.5 font-semibold text-slate-500">Total w/ earnout</td>
                      {preview.total.map((v, i) => <td key={i} className="p-1.5 text-right text-slate-700 dark:text-slate-400">{v != null ? money(v) : "—"}</td>)}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {basisDiffers && multiples && (
            <div className="rounded-md bg-amber-50 px-3 py-1.5 text-[11px] text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
              At current EBITDA ({money(ebitda!)}) this offer shows as{" "}
              <span className="font-bold">{formatEvRange(multiples.low * ebitda!, multiples.high * ebitda!)}</span>{" "}
              ({formatMultipleRange(multiples.low, multiples.high)}) across the tracker.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={cancel}>Cancel</Button>
          <Button size="sm" disabled={!canSave} onClick={save}>
            {bulk.isPending ? "Saving…" : idx + 1 < queue.length ? "Save & Next" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
